import moment, { Moment } from 'moment';

const DATE_FORMAT = 'YYYY-MM-DD';

export type DateRange = {
  start: string;
  end: string;
};

export type CoveragePlan =
  | { status: 'covered' }
  | { status: 'fetch'; missing: DateRange; nextCovered: DateRange };

export type CoverageKeyParts = {
  propertyId: number;
  adults: number;
  children: number;
  childAges: number[];
  promoRateCode?: string;
  rateCode?: string;
  roomCode?: string;
};

export const buildCoverageKey = ({
  propertyId,
  adults,
  children,
  childAges,
  promoRateCode,
  rateCode,
  roomCode,
}: CoverageKeyParts) =>
  [
    propertyId,
    adults,
    children,
    [...childAges].sort((a, b) => a - b).join('.'),
    promoRateCode ?? '',
    rateCode ?? '',
    roomCode ?? '',
  ].join('|');

export const monthWindow = ({ start, months }: { start: Moment; months: number }): DateRange => {
  const firstMonth = start.clone().startOf('M');

  return {
    start: firstMonth.format(DATE_FORMAT),
    end: firstMonth
      .clone()
      .add(months - 1, 'M')
      .endOf('M')
      .format(DATE_FORMAT),
  };
};

const dayAfter = (date: string) => moment(date).add(1, 'day').format(DATE_FORMAT);
const dayBefore = (date: string) => moment(date).subtract(1, 'day').format(DATE_FORMAT);

const touches = ({ requested, covered }: { requested: DateRange; covered: DateRange }) =>
  requested.start <= dayAfter(covered.end) && requested.end >= dayBefore(covered.start);

export const planCoverage = ({
  requested,
  covered,
}: {
  requested: DateRange;
  covered: DateRange | null;
}): CoveragePlan => {
  const fetchWholeWindow: CoveragePlan = {
    status: 'fetch',
    missing: requested,
    nextCovered: requested,
  };

  if (!covered || !touches({ requested, covered })) {
    return fetchWholeWindow;
  }

  const startsBefore = requested.start < covered.start;
  const endsAfter = requested.end > covered.end;

  if (startsBefore && endsAfter) {
    return fetchWholeWindow;
  }

  if (endsAfter) {
    return {
      status: 'fetch',
      missing: { start: dayAfter(covered.end), end: requested.end },
      nextCovered: { start: covered.start, end: requested.end },
    };
  }

  if (startsBefore) {
    return {
      status: 'fetch',
      missing: { start: requested.start, end: dayBefore(covered.start) },
      nextCovered: { start: requested.start, end: covered.end },
    };
  }

  return { status: 'covered' };
};
